import { Injectable, inject, OnDestroy } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { TableFilteringService, TableFilters, TableRowModel } from '../shared/table-filtering.service';
import { Observation, BundleEntry } from 'fhir/r4';
import { backendEndPointToken } from '../app.config';
import { HttpContext } from '@angular/common/http';
import { DisableCountParam, LoadingUIEnabled } from '../loading.interceptor';
import { UtilityService } from '../shared/utility.service';
import { ValueToStringPipe } from '../shared/value-to-string.pipe';
import { ResourceRefreshService } from '../shared/resource-refresh.service';
import { Subject, takeUntil } from 'rxjs';

export interface ObservationRow extends TableRowModel {
  rawResource: Observation;
  codeDisplay?: string;
  displayValue?: string;
  codeAndValue?: string;
  categoryDisplay?: string;
  categoryCodes?: string[];
  effectiveDateIso?: string;
  displayDate?: string;
  displayTime?: string;
  isBloodPressure?: boolean;
  bpText?: string;
  performerCount?: number;
  firstPerformerRef?: string | null;
}

@Injectable({ providedIn: 'root' })
export class ObservationsSearchService implements OnDestroy {
  private readonly tableFiltering = inject<TableFilteringService<Observation, ObservationRow>>(TableFilteringService);
  private readonly backendUrl = inject(backendEndPointToken);
  private readonly utility = inject(UtilityService);
  private readonly silentContext = new HttpContext().set(LoadingUIEnabled, false);
  private readonly refreshService = inject(ResourceRefreshService);
  private readonly destroy$ = new Subject<void>();

  private readonly internalDataSource = new MatTableDataSource<ObservationRow>();
  readonly loading$ = this.tableFiltering.loading$;
  readonly total$ = this.tableFiltering.total$;
  readonly displayedTotal$ = this.tableFiltering.displayedTotalSubject;
  readonly unknownTotal$ = this.tableFiltering.unknownTotal$;
  readonly error$ = this.tableFiltering.error$;

  constructor() {
    this.refreshService.onRefresh('Observation').pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.refresh(true);
    });
  }

  init(patientId: string, options: { pageSize?: number } = {}): void {
    const pageSize = options.pageSize ?? 25;
    const defaultQuery = patientId ? `/Observation?subject=Patient/${encodeURIComponent(patientId)}` : '/Observation';
// $#$// this.displayedTotal$.subscribe(val => {console.log('Displayed total updated:', val); /* debugger; */});
    this.tableFiltering.init({
      resourceType: 'Observation',
      defaultQuery,
      dataSource: this.internalDataSource,
      baseUrl: this.backendUrl,
      pageSize,
      transformEntry: (resource, entry) => this.transformEntry(resource, entry),
      httpOptions: { context: this.silentContext.set(DisableCountParam, true) }
    });
  }

  get dataSource(): MatTableDataSource<ObservationRow> {
    return this.internalDataSource;
  }

  search(filters: TableFilters): void {
    this.tableFiltering.search(filters);
  }

  changePage(pageIndex: number): void {
    this.tableFiltering.changePage(pageIndex);
  }

  refresh(disableCache: boolean = false): void {
    this.tableFiltering.refreshCurrentPage(undefined, disableCache);
  }

  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.tableFiltering.destroy();
    this.internalDataSource.disconnect();
    this.internalDataSource.data = [];
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  private transformEntry(resource: Observation, entry?: BundleEntry<Observation>): ObservationRow {
    const fallbackId = resource.id ?? entry?.resource?.id ?? entry?.fullUrl ?? `Observation-${Date.now()}`;

    const code = resource.code;
    const codeDisplay = (code?.text || code?.coding?.[0]?.display || code?.coding?.[0]?.code || '').toString().trim();

    const effective = (resource.effectiveDateTime || resource.issued) as any;
    const effectiveIso = effective ? new Date(effective).toISOString() : undefined;

    const valuePipe = new ValueToStringPipe(this.utility);
    // Blood pressure special handling
    const isBp = this.isBloodPressure(resource);
    const bpText = isBp ? this.bpDisplayFromObs(resource) : undefined;

    const displayValue = (isBp && bpText) ? bpText : (valuePipe.transform(resource) || '');
    const codeAndValue = [codeDisplay, displayValue].filter(Boolean).join(' — ');

    const categoryCodes = this.getObservationCategoryCodes(resource);
    const categoryDisplay = (Array.isArray(resource.category) && resource.category.length)
      ? resource.category.map(c => c.text || c.coding?.[0]?.display).filter(Boolean).join(', ')
      : '';

    const performerCount = Array.isArray(resource.performer) ? resource.performer.length : 0;
    const firstPerformerRef = resource.performer?.[0]?.reference ?? null;

    const row: ObservationRow = {
      id: fallbackId,
      resourceType: resource.resourceType ?? 'Observation',
      display: codeDisplay || fallbackId,
      rawResource: resource,
      codeDisplay,
      displayValue,
      'formatted value': displayValue,
      codeAndValue,
      categoryDisplay,
      categoryCodes,
      effectiveDateIso: effectiveIso,
      'effective Date': effectiveIso,
      displayDate: effectiveIso ? new Date(effectiveIso).toISOString() : undefined,
      displayTime: effectiveIso ? new Date(effectiveIso).toISOString() : undefined,
      isBloodPressure: isBp,
      bpText,
      performerCount,
      firstPerformerRef
    };
console.log(row);
    return row;
  }

  // Helpers copied from component logic (kept minimal and focused)
  private normalizeCategoryValue(value: unknown): string {
    return (value ?? '').toString().trim().toLowerCase().replace(/\s+/g, '-');
  }

  private getObservationCategoryCodes(observation: any): string[] {
    const categories = Array.isArray(observation?.category) ? observation.category : [];
    const fromCodings = categories
      .flatMap((cat: any) => Array.isArray(cat?.coding) ? cat.coding : [])
      .map((coding: any) => this.normalizeCategoryValue(coding?.code))
      .filter(Boolean);

    const fromTexts = categories
      .map((cat: any) => this.normalizeCategoryValue(cat?.text))
      .filter(Boolean);

    return Array.from(new Set([...fromCodings, ...fromTexts]));
  }

  private isBloodPressure(obs: any): boolean {
    const codeable = obs?.code;
    const codeStr = (codeable?.text || '').toLowerCase();
    const coding = Array.isArray(codeable?.coding) ? codeable.coding : [];
    const hasPanelCode = coding.some((c: any) => c?.code === '85354-9');
    const mentionsBp = codeStr.includes('blood pressure') || coding.some((c: any) => (c?.display || '').toLowerCase().includes('blood pressure'));
    const components = Array.isArray(obs?.component) ? obs.component : [];
    const hasSys = components.some((c: any) => c?.code?.coding?.some((k: any) => k?.code === '8480-6'));
    const hasDia = components.some((c: any) => c?.code?.coding?.some((k: any) => k?.code === '8462-4'));
    return hasPanelCode || mentionsBp || (hasSys && hasDia);
  }

  private bpDisplayFromObs(obs: any): string {
    const components = Array.isArray(obs?.component) ? obs.component : [];
    const getVal = (code: string) => {
      const comp = components.find((c: any) => c?.code?.coding?.some((k: any) => k?.code === code));
      const v = comp?.valueQuantity?.value;
      const unit = comp?.valueQuantity?.unit || comp?.valueQuantity?.code || '';
      return { v, unit };
    };
    const sys = getVal('8480-6');
    const dia = getVal('8462-4');
    if (sys?.v != null && dia?.v != null) {
      const unit = (sys.unit || dia.unit || '').replace('mm[Hg]', 'mmHg') || 'mmHg';
      return `${sys.v}/${dia.v} ${unit}`.trim();
    }
    return '';
  }
}
